import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SceneBack } from '@/shared/components/SceneBack';
import { CHARACTERS, findCharacter, portraitFor } from '../data/characters';

/**
 * Spar flow entry — "Choose Your Adversary".
 *
 * Same gesture as Watch: pick a figure, the selection glows briefly, then we
 * auto-advance into the chat. One pick instead of two, but otherwise the two
 * flows feel identical.
 */
export function Spar() {
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const advanceTimerRef = useRef<number | null>(null);

  useEffect(() => {
    document.title = 'The Ring — The Arena';
  }, []);

  const selected = findCharacter(selectedId);

  // Auto-advance once a figure is locked in. Short delay lets the user see the
  // selection register (portrait glow + "vs You" banner). Clearing the pick
  // during the delay cancels the transition.
  useEffect(() => {
    if (!selected) return;
    advanceTimerRef.current = window.setTimeout(() => {
      navigate(`/arena/spar/${selected.id}`);
    }, 650);
    return () => {
      if (advanceTimerRef.current !== null) {
        clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = null;
      }
    };
  }, [selected, navigate]);

  const handlePick = (id: string) => {
    setSelectedId((curr) => (curr === id ? null : id));
  };

  return (
    <main className="scene scene--setup">
      <div
        className="scene__image scene__image--spar"
        role="img"
        aria-label="Gladiators walking into the arena through opened gates"
      />
      <div className="scene__vignette" />

      <SceneBack />

      <div className="scene__content scene__content--setup">
        <p className="scene__eyebrow">— Step Into the Ring —</p>
        <h1 className="scene__title scene__title--setup">
          Choose Your Adversary
        </h1>

        <ul className="roster" aria-label="Available adversaries">
          {CHARACTERS.map((c) => {
            const isSelected = selectedId === c.id;
            return (
              <li key={c.id} className="roster__item">
                <button
                  type="button"
                  className={`roster__btn${isSelected ? ' is-selected' : ''}`}
                  onClick={() => handlePick(c.id)}
                  aria-pressed={isSelected}
                  title={c.blurb}
                >
                  <span className="roster__portrait" aria-hidden="true">
                    <img src={portraitFor(c.id)} alt="" loading="lazy" />
                  </span>
                  <span className="roster__name">{c.name}</span>
                  <span className="roster__era">{c.era}</span>
                </button>
              </li>
            );
          })}
        </ul>

        {selected && (
          <p className="pair">
            <em className="pair__name">{selected.name}</em>
            <span className="pair__divider" aria-hidden="true">
              vs
            </span>
            <em className="pair__name">You</em>
          </p>
        )}
      </div>
    </main>
  );
}
